/**
 * check user details saved or not?
 *
 */

import axios, { AxiosRequestHeaders } from 'axios';
import { useUserDetailsGlobalStore } from './userDetailsGlobalStore';
import { UserDetailsGlobalStoreDataType } from './userDetailsGlobalStore.types';
import { canUseDOM } from '@marriott/shared/mi-helper-utils';

let isApiCallTriggered = false;
let inFlightUserDetailsPromise: Promise<UserDetailsGlobalStoreDataType> | null = null;

const capatilizeFirstLetter = (str: string) => {
  if (str) {
    const arr = str.split('');
    arr.shift();
    return `${str?.charAt(0).toUpperCase()}${arr.join('')}`;
  }
  return str;
};

export enum UserDetailsURL {
  /**
   * create enum for userDetails URL
   * other app also can use if required
   */
  urlPrefix = '/mi',
  v1 = '/v1',
  v2 = '/v2',
  appName = '/phoenix-account-auth',
  endPoint = '/userDetails',
}

const fetchData = async (customHeaders?: AxiosRequestHeaders): Promise<UserDetailsGlobalStoreDataType> => {
  /**
   * expose this function to use by all apps
   * if required we will pass the url in this function
   */
  const { setUserInfo } = useUserDetailsGlobalStore.getState();

  // If a call is already in-flight, return the existing promise
  if (inFlightUserDetailsPromise) {
    return inFlightUserDetailsPromise;
  }

  isApiCallTriggered = true;
  const locale = canUseDOM && sessionStorage.getItem('SUBDIRECTORYCONTEXTPATH');
  const localePrefix = locale ? `/${locale}` : '';

  const url = `${localePrefix}${UserDetailsURL.urlPrefix}${UserDetailsURL.appName}${UserDetailsURL.v2}${UserDetailsURL.endPoint}`;

  inFlightUserDetailsPromise = axios
    .get<UserDetailsGlobalStoreDataType>(url, {
      headers: {
        'Content-Type': 'application/json',
        ...customHeaders,
      },
    })
    .then(({ data }) => {
      const normalized: UserDetailsGlobalStoreDataType = {
        ...data,
        name: capatilizeFirstLetter((data?.headerSubtext?.consumerName ?? '').toLowerCase()),
        isUserDetailsSaved: true,
      };
      setUserInfo(normalized, data?.status === 'SUCCESS');
      return normalized;
    })
    .catch(e => {
      /**
       * handle error at this place ,we have to use the logger
       */
      // TODO: logger.error('fetchUserDetails failed', e);
      throw e;
    })
    .finally(() => {
      isApiCallTriggered = false;
      inFlightUserDetailsPromise = null; // allow new calls to create a fresh promise
    });

  return inFlightUserDetailsPromise;
};

/**
 * Get user info from store or fetch from API if not available
 * @param forceFetchUserDetails - Force API call even if data exists
 * @param customHeaders - Optional custom headers for the request
 * @returns Promise<User details> from store or API
 */
export const getUserInfo = async (
  forceFetchUserDetails: boolean = false,
  customHeaders?: AxiosRequestHeaders
): Promise<UserDetailsGlobalStoreDataType> => {
  const state = useUserDetailsGlobalStore.getState(); // read from store
  const { isUserDetailsSaved } = state;

  if (forceFetchUserDetails || !isUserDetailsSaved) {
    return fetchData(customHeaders);
  }

  return state;
};

/***
 * array of arguments
 * some are defined E.G
 */
export const getUserDetails = (forceFetchUserDetails: boolean = false, customHeaders?: AxiosRequestHeaders) => {
  /**
   * expose this function to use by all apps
   * if required we will pass the url in this function
   */
  const data = useUserDetailsGlobalStore.getState(); // fetch datra from userstore
  const { isUserDetailsSaved } = data;
  if (forceFetchUserDetails || !isUserDetailsSaved) {
    /**
     * fetch user details if data is not available in zustand
     */

    // resetUserDetails(); //TODO: if needed in future
    fetchData(customHeaders);
  }
};

/**
 *
 */

export const resetUserDetails = (immediateUpdate: boolean = false) => {
  /**
   * reset user details
   * if@ page is redirected on user action
   * need to reset the store so that we can fetch the user details and save new data
   */
  if (!immediateUpdate) {
    /**
     * clear session storage so
     * store will clear on page refresh
     */
    sessionStorage.removeItem('UserDetailsGlobalStore');
  } else {
    /**
     * store will be clear when we call this function
     */
    const { resetUserInfo } = useUserDetailsGlobalStore.getState();
    resetUserInfo();
  }
};
